import { useRef, useState, useEffect } from 'react';

const CURSIVE_FONTS = [
  { label: 'Signature 1', font: 'Dancing Script, cursive' },
  { label: 'Signature 2', font: 'Great Vibes, cursive' },
  { label: 'Signature 3', font: 'Pacifico, cursive' },
];

// Same rules the server applies (server/utils/signature.js): enough drawn pixels over a minimum width and height
const MIN_PIXELS = 300, MIN_WIDTH = 40, MIN_HEIGHT = 20;
const looksLikeSignature = (canvas) => {
  const { width, height } = canvas;
  const data = canvas.getContext('2d').getImageData(0, 0, width, height).data;
  let pixels = 0, minX = width, maxX = -1, minY = height, maxY = -1;
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    if (data[(y * width + x) * 4 + 3] > 40) {
      pixels++;
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
  }
  return pixels >= MIN_PIXELS && maxX - minX + 1 >= MIN_WIDTH && maxY - minY + 1 >= MIN_HEIGHT;
};

// A typed signature: the person's name (passed in from the form) is shown in a few handwriting styles; they pick one,
// check the preview, then press "Use this signature". Nothing reaches the form until then.
export default function SignaturePad({ label, name: fullName = '', value, onChange }) {
  const canvasRef = useRef(null);
  const [fontIdx, setFontIdx]     = useState(0);
  const [error, setError]         = useState('');

  useEffect(() => {
    // Load Google Fonts for the signature styles
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Great+Vibes&family=Pacifico&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  const name = String(fullName || '').trim();

  // Redraw the preview whenever the name or style changes (once the chosen font has loaded)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    let cancelled = false;
    const font = `52px ${CURSIVE_FONTS[fontIdx].font}`;
    const paint = () => {
      if (cancelled) return;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (!name) return;
      ctx.font = font;
      ctx.fillStyle = '#1a1a2e';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(name, canvas.width / 2, canvas.height / 2, canvas.width - 40);
    };
    if (document.fonts?.load) document.fonts.load(font, name).then(paint, paint); else paint();
    return () => { cancelled = true; };
  }, [name, fontIdx, value]);

  const useSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas || name.length < 2) { setError('Please fill in your name first.'); return; }
    if (!looksLikeSignature(canvas)) { setError('That signature is too small. Please check the name above.'); return; }
    setError('');
    onChange(canvas.toDataURL('image/png'));
  };

  const changeSignature = () => { onChange(''); setError(''); };

  const frame = { borderRadius:'var(--radius)', background:'#fafafa', overflow:'hidden' };
  const heading = <div style={{ fontSize:'13px', fontWeight:'500', color:'var(--text)', marginBottom:'8px' }}>{label}</div>;

  // Already created (or loaded from a saved record): show it, with a way to start over
  if (value) {
    return (
      <div>
        {heading}
        <div style={{ position:'relative', border:'1.5px solid var(--green)', ...frame }}>
          <img src={value} alt="Your signature" style={{ width:'100%', height:'120px', display:'block', objectFit:'contain' }}/>
          <button type="button" onClick={changeSignature}
            style={{ position:'absolute', top:'6px', right:'6px', background:'rgba(255,255,255,0.9)', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', padding:'3px 8px', fontSize:'11px', color:'var(--muted)', cursor:'pointer' }}>
            Change
          </button>
        </div>
        <div style={{ fontSize:'11px', color:'var(--green)', marginTop:'4px' }}>✓ Signature saved</div>
      </div>
    );
  }

  return (
    <div>
      {heading}

      <div style={{ fontSize:'12px', color:'var(--muted)', marginBottom:'8px' }}>
        {name ? 'Choose a style for your signature:' : 'Fill in the name above first, then choose a style for your signature.'}
      </div>
      <div style={{ display:'flex', gap:'6px', marginBottom:'10px' }}>
        {CURSIVE_FONTS.map((f,i)=>(
          <button key={i} type="button" onClick={()=>setFontIdx(i)}
            style={{ flex:1, padding:'6px', border:`${fontIdx===i?'1.5px solid var(--blue)':'1px solid var(--border-md)'}`, borderRadius:'var(--radius)', background:fontIdx===i?'var(--blue-light)':'var(--surface)', cursor:'pointer', fontFamily:f.font, fontSize:'15px', color:fontIdx===i?'var(--blue)':'var(--muted)', overflow:'hidden', whiteSpace:'nowrap' }}>
            {name||'Name'}
          </button>
        ))}
      </div>

      {/* Preview */}
      <div style={{ position:'relative', border:`1.5px solid ${error?'var(--red)':'var(--border-md)'}`, ...frame }}>
        <canvas ref={canvasRef} width={600} height={160} style={{ width:'100%', height:'120px', display:'block' }}/>
        {!name && (
          <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none' }}>
            <span style={{ fontSize:'13px', color:'var(--hint)' }}>Your signature will appear here</span>
          </div>
        )}
      </div>
      {error && <div style={{ fontSize:'11px', color:'var(--red)', marginTop:'4px' }}>{error}</div>}
      <button type="button" onClick={useSignature} disabled={name.length < 2}
        style={{ width:'100%', marginTop:'8px', padding:'9px', borderRadius:'var(--radius)', border:'none', background:name.length<2?'var(--border-md)':'var(--blue)', color:name.length<2?'var(--muted)':'#fff', fontSize:'13px', fontWeight:'500', cursor:name.length<2?'not-allowed':'pointer', fontFamily:'inherit' }}>
        <i className="ti ti-check" style={{ marginRight:'4px' }} aria-hidden="true"/>Use this signature
      </button>
    </div>
  );
}
